import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "./AdminAuthContext";
import { StatusPill, formatINR, formatINRShort, fmtDate, fmtDateTime, Sparkline } from "./widgets";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip,
  PieChart, Pie, Cell,
} from "recharts";
import { ShoppingBag, IndianRupee, Truck, Clock } from "lucide-react";

const RANGES = ["7d", "30d", "90d", "all"];
const PIE_COLORS = { pending: "#F59E0B", processing: "#3B82F6", shipped: "#8B5CF6", delivered: "#10B981", cancelled: "#EF4444" };

export default function AdminDashboard() {
  const { apiFetch } = useAdminAuth();
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [chartMode, setChartMode] = useState("revenue");
  const nav = useNavigate();

  useEffect(() => {
    apiFetch(`/admin/dashboard?range=${range}`).then(({ data }) => setData(data)).catch(() => {});
  }, [range, apiFetch]);

  if (!data) return <div className="adm-empty">Loading dashboard...</div>;
  const { stats, series, status_breakdown, best_sellers, low_stock, recent_orders, activity } = data;
  const pie = Object.entries(status_breakdown).map(([k, v]) => ({ name: k, value: v }));

  return (
    <>
      <div className="adm-page-head">
        <div>
          <h1 className="adm-h1">Dashboard</h1>
          <div className="adm-h1-sub">Live store performance</div>
        </div>
        <div className="adm-range">
          {RANGES.map((r) => (
            <button key={r} className={range === r ? "active" : ""} onClick={() => setRange(r)}>{r}</button>
          ))}
        </div>
      </div>

      <div className="adm-stats">
        <StatCard icon={<ShoppingBag size={14} />} label="Orders" value={stats.orders.value} delta={stats.orders.delta_pct} spark={series} />
        <StatCard icon={<IndianRupee size={14} />} label="Revenue" value={formatINRShort(stats.revenue.value)} delta={stats.revenue.delta_pct} spark={series} />
        <StatCard icon={<Truck size={14} />} label="Shipped" value={stats.shipped.value} />
        <StatCard icon={<Clock size={14} />} label="Pending" value={stats.pending.value} accent="amber" />
      </div>

      <div className="adm-grid-2">
        <div className="adm-card">
          <div className="adm-card-h">
            <div>
              <div className="adm-card-title">Performance</div>
              <div className="adm-card-sub">{chartMode} / {range}</div>
            </div>
            <div className="adm-range">
              <button className={chartMode === "revenue" ? "active" : ""} onClick={() => setChartMode("revenue")}>Revenue</button>
              <button className={chartMode === "orders" ? "active" : ""} onClick={() => setChartMode("orders")}>Orders</button>
            </div>
          </div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 8, right: 12, left: -4, bottom: 0 }}>
                <defs>
                  <linearGradient id="grd" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" stroke="#5A5A62" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#5A5A62" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(v) => chartMode === "revenue" ? formatINRShort(v) : v} width={56} />
                <Tooltip
                  contentStyle={{ background: "#17171A", border: "1px solid #26262C", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#ECECEE" }}
                  formatter={(v) => chartMode === "revenue" ? formatINR(v) : v}
                />
                <Area type="monotone" dataKey={chartMode} stroke="#8B5CF6" strokeWidth={2} fill="url(#grd)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="adm-card">
          <div className="adm-card-h">
            <div className="adm-card-title">Order status</div>
            <div className="adm-card-sub">{range}</div>
          </div>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} stroke="#0A0A0B" strokeWidth={2}>
                  {pie.map((p) => <Cell key={p.name} fill={PIE_COLORS[p.name] || "#5A5A62"} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#17171A", border: "1px solid #26262C", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
            {pie.map((p) => {
              const total = pie.reduce((s, x) => s + x.value, 0) || 1;
              const pct = ((p.value / total) * 100).toFixed(0);
              return (
                <div key={p.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 8, height: 8, background: PIE_COLORS[p.name] || "#5A5A62", borderRadius: 50 }} />
                    <span style={{ textTransform: "capitalize", color: "#B6B6BD" }}>{p.name}</span>
                  </span>
                  <span className="adm-mono" style={{ color: "#8B8B93" }}>{p.value} / {pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="adm-grid-2-bs">
        <div className="adm-card">
          <div className="adm-card-h">
            <div className="adm-card-title">Recent orders</div>
            <div className="adm-card-sub">Latest 8</div>
          </div>
          <table className="adm-table">
            <thead><tr><th>Order</th><th>Customer</th><th>Status</th><th style={{ textAlign: "right" }}>Total</th></tr></thead>
            <tbody>
              {recent_orders.length === 0 ? <tr><td colSpan={4} className="adm-empty">No orders yet.</td></tr> :
                recent_orders.map((o) => (
                <tr key={o.id} className="clickable" onClick={() => nav(`/admin/orders?id=${o.id}`)}>
                  <td className="adm-mono">{o.id}</td>
                  <td>{o.customer_name}<div className="adm-cell-name-sub">{o.customer_email}</div></td>
                  <td><StatusPill status={o.status} /></td>
                  <td className="adm-mono" style={{ textAlign: "right" }}>{formatINR(o.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="adm-card">
          <div className="adm-card-h">
            <div className="adm-card-title">Best sellers</div>
            <div className="adm-card-sub">By units, {range}</div>
          </div>
          <table className="adm-table">
            <thead><tr><th>Product</th><th style={{ textAlign: "right" }}>Units</th><th style={{ textAlign: "right" }}>Revenue</th></tr></thead>
            <tbody>
              {best_sellers.length === 0 ? <tr><td colSpan={3} className="adm-empty">No paid orders in this period.</td></tr> :
                best_sellers.map((p) => (
                <tr key={p.product_id}>
                  <td>{p.name}<div className="adm-cell-name-sub">{p.product_id}</div></td>
                  <td className="adm-mono" style={{ textAlign: "right" }}>{p.units}</td>
                  <td className="adm-mono" style={{ textAlign: "right" }}>{formatINR(p.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="adm-grid-2-bs">
        <div className="adm-card">
          <div className="adm-card-h">
            <div className="adm-card-title">Low stock</div>
            <div className="adm-card-sub">Stock {"<="} 5</div>
          </div>
          <table className="adm-table">
            <thead><tr><th>Product</th><th>SKU</th><th style={{ textAlign: "right" }}>Stock</th></tr></thead>
            <tbody>
              {low_stock.length === 0 ? <tr><td colSpan={3} className="adm-empty">All stock healthy.</td></tr> :
                low_stock.map((p) => (
                <tr key={p.id} className="clickable" onClick={() => nav(`/admin/inventory`)}>
                  <td><div className="adm-cell-name"><img src={p.images?.[0]} alt="" className="adm-thumb" /><div className="adm-cell-name-text"><span>{p.name}</span></div></div></td>
                  <td className="adm-mono">{p.sku}</td>
                  <td className="adm-mono" style={{ textAlign: "right", color: p.stock_qty <= 2 ? "#EF4444" : "#F59E0B" }}>{p.stock_qty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="adm-card">
          <div className="adm-card-h">
            <div className="adm-card-title">Activity</div>
            <div className="adm-card-sub">Live feed</div>
          </div>
          <div className="adm-activity">
            {activity.length === 0 ? <div className="adm-empty">No activity yet.</div> :
              activity.map((a) => (
                <div key={a.id} className="adm-activity-row">
                  <div><strong>{a.actor || "system"}</strong> {a.message}</div>
                  <div className="adm-activity-time">{fmtDateTime(a.created_at)} / {a.type}</div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </>
  );
}

function StatCard({ icon, label, value, delta, spark, accent }) {
  return (
    <div className="adm-stat">
      <div className="adm-stat-label">{icon} {label}</div>
      <div className="adm-stat-value" style={accent === "amber" ? { color: "#F59E0B" } : {}}>{value}</div>
      {typeof delta === "number" && (
        <div className={`adm-stat-delta ${delta >= 0 ? "up" : "down"}`}>
          {delta >= 0 ? "\u25B2" : "\u25BC"} {Math.abs(delta)}% vs prev
        </div>
      )}
      {spark && spark.length > 1 && <div className="adm-stat-spark"><Sparkline data={spark} /></div>}
    </div>
  );
}
